import { countries, currentMsMarket } from "@ms/us/src/@ms/gatsby-site/config/navigation";
import { graphql, useStaticQuery } from "gatsby";
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next";

import {
  amexLogoSvgSrc,
  applePayLogoCardSvgSrc,
  discoverLogoSvgSrc,
  googlePayLogoSvgSrc,
  klarnaWhiteLogoSvgSrc,
  masterCardLogoSvgSrc,
  paypalLogoSvgSrc,
  shopPayLogoSvgSrc,
  unionPayLogoSvgSrc,
  venmoMarkSvgSrc,
  visaLogoSvgSrc,
} from "@ms/gatsby-theme";

import { socialLinks } from "@ms/gatsby-site/src/config/footer";
import { SUPPORT_EMAIL_ADDRESS_LOCALES } from "@ms/gatsby-site/src/config/support";

import type { StrapiSiteNode } from "@/types/strapi";
import type { FooterDataProps } from "@ms/gatsby-theme";

const FOOTER_QUERY = graphql`
  query FooterQuery {
    allStrapiSite(filter: { Name: { ne: "__DO_NOT_REMOVE" } }) {
      nodes {
        Instance
        footer {
          ...StrapiFooter
        }
      }
    }
  }
`;

/**
 * Use Footer Data
 *
 * this takes in the footer config for the given instance and creates the footer Data to allow
 * all footer component to live in our gatsby-theme component library so we can use
 * the same footer in multiple places
 */
export const useBlogFooterData = (): FooterDataProps => {
  const { language: currentLanguage } = useI18next();
  const { allStrapiSite } = useStaticQuery(FOOTER_QUERY);

  const siteFooterData = allStrapiSite.nodes.find(
    (site: StrapiSiteNode) => site.Instance.toUpperCase() === "US",
  )?.footer;

  const { footerGroup, footerLegalLinks, showFooterAddress, valuePropModule } = siteFooterData;

  const { t } = useTranslation("footer");

  const paymentMethods = [
    visaLogoSvgSrc,
    masterCardLogoSvgSrc,
    amexLogoSvgSrc,
    discoverLogoSvgSrc,
    unionPayLogoSvgSrc,
    applePayLogoCardSvgSrc,
    googlePayLogoSvgSrc,
    shopPayLogoSvgSrc,
    klarnaWhiteLogoSvgSrc,
    venmoMarkSvgSrc,
    paypalLogoSvgSrc,
  ];

  const SUPPORT_EMAIL = SUPPORT_EMAIL_ADDRESS_LOCALES.find(
    (n) => n.lang === currentLanguage,
  )?.value;

  const headers = {
    footerPrivacyPreferences: t("footerPrivacyPreferences", {
      defaultValue: "Privacy Preferences",
    }),
    footerAcceptedPaymentMethods: t("footerAcceptedPaymentMethods", {
      defaultValue: "Accepted payment methods",
    }),
    footerEmailTitle: t("footerEmailTitle", {
      defaultValue: "Get Early Access to New Products Below",
    }),
    footerSocialTitle: t("footerSocialTitle", { defaultValue: "Follow us on social" }),
    footerAllRightsReserved: t("footerAllRightsReserved", { defaultValue: "All Rights Reserved" }),
    footerRegisteredSymbol: t("footerRegisteredSymbol", {
      defaultValue:
        "®️ symbol designates a registered trademark of Manscaped, LLC in the United States and elsewhere.",
    }),
  };

  return {
    currentMsMarket: currentMsMarket,
    supportEmail: SUPPORT_EMAIL ?? "",
    headers: headers,
    valuePropModule: valuePropModule,
    footerLinks: footerGroup,
    footerLegalLinks: footerLegalLinks,
    showFooterAddress: showFooterAddress,
    availablePaymentMethods: paymentMethods,
    socialLinks: socialLinks.map((link) => ({
      ...link,
      title: t(link.title, { defaultValue: link.title }),
    })),
    countries: countries.map((country) => ({
      ...country,
      displayName: t(country.displayName, { defaultValue: country.displayName }),
    })),
    EmailMarketingSignUpForm: undefined,
    isBlog: true,
  };
};
